import React, { useState, useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import useAudioPlayer from '../hooks/useAudioPlayer'
import renderFormattedText from '../utils/renderFormattedText'

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '8px 0',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    text: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '170%',
        color: '#FFFFFF',
        textAlign: 'center',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        maxWidth: '100%',
        transition: 'opacity 300ms ease',
    },
}))

/**
 * TextInteraction — plain text display (interactiveElementType === 4).
 * Non-interactive: immediately reports as complete.
 *
 * AudioPlayType support: when the text is in L2 (textLanguageId === l2LanguageId)
 * and has an audio URL, plays audio before or after rendering the text.
 *
 * Props:
 *   onDisplayComplete(testOptionId, { hadAudio }) — optional, called when the
 *       interaction has finished its full display cycle (audio + text visible).
 *       Used by TestGenericNode for sequential rendering.
 */
const TextInteraction = ({
    testOption,
    onComplete,
    onDisplayComplete,
    audioMap,
    l2LanguageId,
    deferAudioPlayBefore,
    textAlign = 'center',
}) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const [textVisible, setTextVisible] = useState(false)
    const hasCompletedRef = useRef(false)
    const hasDisplayCompletedRef = useRef(false)

    const isL2 = l2LanguageId != null && testOption.textLanguageId === l2LanguageId
    const audioPlayType = testOption.audioPlayType ?? AudioPlayType.DontPlay
    const audioBlobUrl = testOption.textAudioUrl && audioMap ? audioMap[testOption.textAudioUrl] : null
    const shouldPlayAudio = isL2 && audioBlobUrl && audioPlayType !== AudioPlayType.DontPlay

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            const isPlayBefore = shouldPlayAudio && audioPlayType === AudioPlayType.PlayBefore
            const isPlayAfter = shouldPlayAudio && audioPlayType === AudioPlayType.PlayAfter

            if (isPlayBefore && !deferAudioPlayBefore) {
                // Standard PlayBefore: audio first, then text
                await audioPlayer.playAudio(audioBlobUrl)
                if (cancelled) return
                setTextVisible(true)
            } else {
                // Show text first (PlayAfter, deferred PlayBefore, or no audio)
                setTextVisible(true)

                if (isPlayBefore && deferAudioPlayBefore) {
                    // Deferred PlayBefore: text appears, then audio plays immediately
                    await new Promise((r) => setTimeout(r, 200))
                    if (cancelled) return
                    await audioPlayer.playAudio(audioBlobUrl)
                    if (cancelled) return
                } else if (isPlayAfter) {
                    await new Promise((r) => setTimeout(r, 300))
                    if (cancelled) return
                    await audioPlayer.playAudio(audioBlobUrl)
                    if (cancelled) return
                }
            }

            if (!hasCompletedRef.current) {
                hasCompletedRef.current = true
                if (onComplete) {
                    onComplete({
                        testOptionId: testOption.testOptionId,
                        answers: [],
                    })
                }
            }

            if (!hasDisplayCompletedRef.current) {
                hasDisplayCompletedRef.current = true
                if (onDisplayComplete) {
                    onDisplayComplete(testOption.testOptionId, { hadAudio: shouldPlayAudio })
                }
            }
        }

        run()

        return () => {
            cancelled = true
            audioPlayer.stopAudio()
        }
    }, [])

    if (!textVisible) return null

    const alignItems = textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'

    return (
        <div className={classes.container} style={{ alignItems }}>
            <Typography className={classes.text} style={{ textAlign }}>{renderFormattedText(testOption.text)}</Typography>
        </div>
    )
}

export default TextInteraction
